import { Parser } from 'html-to-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { forwardRef, useEffect, useState } from 'react';

import { ReactElement } from 'react';
import { QualioDocument } from '../../../api/model/document';
import {
  setupAttachmentPreviewListener,
  setupImagePreviewListener,
} from '../../../config/handlers';
import { getDocumentEditorGlobalWindowObject } from '../../../util/AppUtils';
import { DocumentPresentationUtils } from '../../../util/DocumentPresentationUtils';
import { DocumentLayout } from '../DocumentLayout/DocumentLayout';

export type DocumentHTMLViewerProps = {
  viewDocument: QualioDocument;
  showSidebar: boolean;
  showTOC: boolean;
  setLoaded: boolean;
  handleAttachmentClick?: (attachmentId: string) => void;
};

const replaceSectionHeaderIds = () => {
  const tableOfContentsLinks = Array.from(
    document.querySelectorAll('.table-of-contents a'),
  );
  const sectionHeadings = Array.from(
    document.querySelectorAll('h1.section-heading'),
  );
  const sectionHeadingsMap = sectionHeadings.reduce(
    (obj, sh) => {
      obj[sh.textContent || 'section'] = sh;
      return obj;
    },
    {} as Record<string, Element>,
  );

  tableOfContentsLinks.forEach((link) => {
    const linkText = link.textContent || 'section';
    const sectionHeading = sectionHeadingsMap[linkText];

    if (!sectionHeading) {
      return;
    }
    link.setAttribute('href', `#${sectionHeading.getAttribute('id')}`);
  });
};

export const DocumentHTMLViewer = forwardRef<
  HTMLDivElement,
  DocumentHTMLViewerProps
>(
  (
    { viewDocument, showSidebar, showTOC, setLoaded, handleAttachmentClick },
    ref,
  ) => {
    const { santitizeHtmlView } = useFlags();

    const [htmlElement, setHtmlElement] = useState<ReactElement | null>(null);

    useEffect(() => {
      const htmlToReactParser = new Parser();
      if (!htmlElement) {
        DocumentPresentationUtils.determineHtmlDocumentContentFromDocument(
          viewDocument,
          false,
          false,
          false,
          santitizeHtmlView,
        )
          .then((html) => {
            const parsedHtml = htmlToReactParser.parse(html);
            setHtmlElement(parsedHtml);
          })
          .catch((e) => console.error(e));
      }
    }, [viewDocument, santitizeHtmlView, htmlElement]);

    useEffect(() => {
      if (!htmlElement) return;

      const contentElement = document.querySelector(
        '#documentHTMLViewerContent',
      );

      if (!contentElement) return;

      if (handleAttachmentClick) {
        setupAttachmentPreviewListener(contentElement, handleAttachmentClick);
        setupImagePreviewListener(contentElement, handleAttachmentClick);
      }
      replaceSectionHeaderIds();
      CKReusableContent?.ReusableContent?.updateEmbedLabels(
        viewDocument.company_id,
        process.env.REACT_APP_MEDTECH_API_URL,
        viewDocument.status_id,
      );
    }, [
      handleAttachmentClick,
      htmlElement,
      viewDocument.company_id,
      viewDocument.status_id,
    ]);

    // Hook for MTFE to know that the editor is loaded
    // to enable the edit and export button
    if (setLoaded) {
      getDocumentEditorGlobalWindowObject().loaded = true;
    }

    if (!htmlElement) {
      return null;
    }

    return (
      <DocumentLayout
        ref={ref}
        sections={viewDocument.sections}
        sectionTitlesVisible={viewDocument.text_sections_titles_visible}
        editorName="documentHTMLViewerContent"
        contentId="documentHTMLViewerContent"
        sidebarId="commentsSidebarPlaceholder"
        dataCy={{
          content: 'documentHTMLViewerContent',
        }}
        isLandscape={viewDocument.orientation === 'landscape'}
      >
        {htmlElement}
      </DocumentLayout>
    );
  },
);
